/**
 * @test-file: useAuth.test.ts
 * @test-coverage: 15 tests (auth flow, admin checks, token handling, redirects)
 * @last-updated: 2025-12-28
 */
import { useState, useEffect, useCallback } from 'react';
const DEFAULT_AUTH_ENDPOINT = 'https://login.weretrade.com/api/auth/me';
const DEFAULT_LOGIN_URL = 'https://login.weretrade.com/login';
const TOKEN_STORAGE_KEY = 'wt_auth_token';
/**
 * Get auth token from localStorage or URL parameter.
 * For .pages.dev/.workers.dev domains, auth redirects include ?session=TOKEN
 */
function getAuthToken() {
    // Check URL parameter first (set during login redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('session');
    if (urlToken) {
        // Store token and clean URL
        localStorage.setItem(TOKEN_STORAGE_KEY, urlToken);
        // Remove session param from URL without reload
        const url = new URL(window.location.href);
        url.searchParams.delete('session');
        window.history.replaceState({}, '', url.toString());
        return urlToken;
    }
    // Check localStorage
    return localStorage.getItem(TOKEN_STORAGE_KEY);
}
/**
 * Clear stored auth token
 */
function clearAuthToken() {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
}
/**
 * Centralized authentication hook for all weretrade apps.
 * Handles auth state, admin checks, and redirects.
 * Supports both cookie-based auth (.weretrade.com) and token-based auth (.pages.dev/.workers.dev).
 *
 * @example
 * // Basic usage - redirects to login if not authenticated
 * const { user, loading, isAuthenticated } = useAuth();
 *
 * @example
 * // Admin-only page
 * const { user, loading, isAdmin, error } = useAuth({ requireAdmin: true });
 *
 * @example
 * // No auto-redirect (e.g., for public pages with optional auth)
 * const { isAuthenticated } = useAuth({ redirectOnUnauthenticated: false });
 *
 * @example
 * // Skip initial auth check (e.g., on login page where user is not authenticated)
 * const { login } = useAuth({ skipInitialCheck: true });
 */
export function useAuth(options = {}) {
    const { requireAdmin = false, redirectOnUnauthenticated = true, redirectUrl, authEndpoint = DEFAULT_AUTH_ENDPOINT, loginUrl = DEFAULT_LOGIN_URL, skipInitialCheck = false, } = options;
    const [state, setState] = useState({
        user: null,
        profile: null,
        loading: true,
        isAdmin: false,
        isAuthenticated: false,
        error: null,
    });
    const checkAuth = useCallback(async () => {
        setState((prev) => ({ ...prev, loading: true, error: null }));
        try {
            // Get auth token (for .pages.dev/.workers.dev domains)
            const token = getAuthToken();
            // Build request headers
            const headers = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            const res = await fetch(authEndpoint, {
                credentials: 'include', // Still include cookies for .weretrade.com domains
                headers,
            });
            if (!res.ok) {
                // Auth failed - clear any stored token
                clearAuthToken();
                // Not authenticated
                if (redirectOnUnauthenticated) {
                    const redirect = redirectUrl || window.location.href;
                    window.location.href = `${loginUrl}?redirect=${encodeURIComponent(redirect)}`;
                    return;
                }
                setState({
                    user: null,
                    profile: null,
                    loading: false,
                    isAdmin: false,
                    isAuthenticated: false,
                    error: null,
                });
                return;
            }
            const data = (await res.json());
            const isAdmin = data.profile?.role === 'admin';
            setState({
                user: data.user,
                profile: data.profile,
                loading: false,
                isAdmin,
                isAuthenticated: true,
                error: requireAdmin && !isAdmin ? 'Access denied. Admin privileges required.' : null,
            });
        }
        catch (err) {
            clearAuthToken();
            setState({
                user: null,
                profile: null,
                loading: false,
                isAdmin: false,
                isAuthenticated: false,
                error: err instanceof Error ? err.message : 'Failed to verify authentication',
            });
        }
    }, [authEndpoint, loginUrl, redirectOnUnauthenticated, redirectUrl, requireAdmin]);
    useEffect(() => {
        if (!skipInitialCheck) {
            checkAuth();
        }
    }, [checkAuth, skipInitialCheck]);
    return { ...state, refresh: checkAuth };
}
/**
 * Simple hook to get current user display name.
 * Returns loading placeholder or display name.
 */
export function useDisplayName(fallback = 'User') {
    const { profile, loading } = useAuth({ redirectOnUnauthenticated: false });
    if (loading)
        return '...';
    return profile?.display_name || profile?.email || fallback;
}
